import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { courseApi } from '../api/client';
import { Course } from '../types';
import { BookOpen, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

export function CoursesPage() {
  const { user }                  = useAuth();
  const navigate                  = useNavigate();
  const [courses, setCourses]     = useState<Course[]>([]);
  const [loading, setLoading]     = useState(true);
  const [enrolling, setEnrolling] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await courseApi.getAll();
        setCourses(res.data.content || []);
      } catch (error: any) {
  if (error.response?.status === 401 || error.response?.status === 403) {
    // Let the interceptor handle this — don't show a separate error
    return;
  }
  toast.error('Failed to load courses');
} finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const handleEnroll = async (e: React.MouseEvent, courseId: string) => {
    // Stop click from bubbling up to the card's navigate
  e.stopPropagation();
  setEnrolling(courseId);
  try {
    await courseApi.enroll(courseId);
    toast.success('Enrolled! 🎉 Go to Dashboard to start learning');
  } catch (error: any) {
    if (error.response?.status === 409) {
      toast.success('Already enrolled — check your Dashboard');
    } else if (error.response?.status === 403) {
      toast.error('Session expired — please login again');
      setTimeout(() => window.location.href = '/login', 1500);
    } else {
      toast.error(
        error.response?.data?.message || 'Enrolment failed — try again'
      );
    }
  } finally {
    setEnrolling(null);
  }
};

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="text-gray-500">Loading courses...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <h1 className="text-2xl font-black text-white mb-2">All Courses</h1>
      <p className="text-gray-400 text-sm mb-8">
        {courses.length} courses available in your school
      </p>

      {courses.length === 0 ? (
        <div className="text-center py-20 text-gray-500">
          <BookOpen className="mx-auto mb-4" size={48} />
          <p>No courses available yet.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map(course => (
            <div
              key={course.id}
              onClick={() => navigate(`/courses/${course.id}`)}
              className="bg-gray-900 border border-gray-800
                         rounded-xl overflow-hidden hover:border-green-400/40
                         transition-colors cursor-pointer"
            >
              {/* Course header */}
              <div className="bg-gradient-to-br from-green-400/10
                              to-blue-500/10 p-5">
                <div className="text-xs text-green-400 font-bold mb-2">
                  {course.subject} · Grade {course.grade} · {course.board}
                </div>
                <h3 className="font-black text-white text-base leading-snug">
                  {course.title}
                </h3>
              </div>

              {/* Course body */}
              <div className="p-5">
                {course.description && (
                  <p className="text-gray-400 text-xs mb-4 line-clamp-2">
                    {course.description}
                  </p>
                )}

                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-1
                                  text-gray-500 text-xs">
                    <Users size={12} />
                    {course.enrolledCount} enrolled
                  </div>
                  <span className="text-xs bg-gray-800 text-gray-400
                                   px-2 py-1 rounded-full">
                    {course.difficulty}
                  </span>
                </div>

                <div className="text-xs text-gray-600 mb-4">
                  by {course.createdByName}
                </div>

                {user?.role === 'STUDENT' && (
                  <button
                    onClick={(e) => handleEnroll(e, course.id)}
                    disabled={enrolling === course.id}
                    className="w-full bg-green-400 text-gray-950
                               font-bold py-2 rounded-lg text-sm
                               hover:bg-green-300 transition-colors
                               disabled:opacity-50"
                  >
                    {enrolling === course.id
                      ? 'Enrolling...'
                      : 'Enroll Now'
                    }
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}